// Ortak biçimlendirme yardımcıları. Daha önce bu iki fonksiyon feature
// dosyalarına birebir olmayan (ufak farklarla) kopyalanmıştı; tek kaynak
// haline getirildi. Farklar, çağrı yerinden seçeneklerle korunur.

/**
 * Veri miktarını (MB) okunur biçime çevirir. 1024 MB ve üzeri GB gösterilir.
 * @param {number|null|undefined} mb - Megabayt cinsinden miktar
 * @param {object} [opts]
 * @param {string} [opts.empty='—'] - Değer yoksa dönecek metin ('' veya '—')
 * @param {1|2} [opts.gbDigits=1] - GB'de ondalık davranışı:
 *   1 → tam sayı GB ondalıksız ("2 GB"), aksi halde tek ondalık ("2.5 GB");
 *   2 → her zaman iki ondalık ("2.00 GB").
 * @returns {string}
 */
export const formatData = (mb, { empty = '—', gbDigits = 1 } = {}) => {
  if (mb == null) return empty;
  if (mb >= 1024) {
    const gb = mb / 1024;
    if (gbDigits === 1) {
      return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    }
    return `${gb.toFixed(gbDigits)} GB`;
  }
  return `${mb} MB`;
};

/**
 * Sınırsız paketleri de kapsayan veri miktarı etiketi.
 *
 * Sınırsız planlarda backend `dataMb` alanını BİLEREK göndermez (null alanlar
 * serialize edilmez) — sağlayıcı hepsine 1024 yazdığı için o değer gerçek bir
 * limit değildir. Bu yüzden karar `dataMb`'nin varlığına göre DEĞİL, yalnızca
 * `isUnlimited` alanına göre verilir. Bkz. src/FRONTEND_INTEGRATION.md §3.
 *
 * @param {Function} t - i18next çeviri fonksiyonu
 * @param {boolean|null|undefined} isUnlimited
 * @param {number|null|undefined} mb
 * @param {object} [opts] - formatData seçenekleri
 * @returns {string}
 */
export const formatDataOrUnlimited = (t, isUnlimited, mb, opts) =>
  isUnlimited ? t('detail.unlimited') : formatData(mb, opts);

/**
 * Paket geçerlilik süresini metne çevirir. Ömürlük (lifetime) paketlerde
 * backend amountDays = 0 döndürür; bunlar "0 gün" değil "Süresiz" gösterilmeli.
 * Sayı olmayan değerler (checkout/sipariş akışında backend'den gelen serbest
 * metin olabilir) olduğu gibi geri döner.
 * @param {Function} t - i18next çeviri fonksiyonu
 * @param {number|string|null|undefined} days - amountDays veya plan.validity
 * @returns {string}
 */
export const formatValidity = (t, days) => {
  const raw = String(days ?? '');
  if (!/^\d+$/.test(raw)) return days ?? '';
  return Number(raw) > 0
    ? t('detail.validity', { days: Number(raw) })
    : t('detail.validityLifetime');
};

/**
 * Tarihi yerelleştirilmiş biçimde döndürür. Geçersiz/boş değerde '—'.
 * @param {string|number|null|undefined} value - Tarih (ISO string veya epoch)
 * @param {string} locale - i18n dili (ör. 'tr', 'en')
 * @param {object} [opts]
 * @param {boolean} [opts.withTime=false] - true → uzun ay + saat:dakika;
 *   false → kısa ay, saat yok.
 * @returns {string}
 */
export const formatDate = (value, locale, { withTime = false } = {}) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const opts = withTime
    ? { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' };
  return d.toLocaleDateString(locale, opts);
};
